import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import CircularProgress from '@mui/material/CircularProgress'

import useOrganizations from '../models/useOrganizations'
import useBilling, { isFreePlan, type PlanRow, type PortalFlow } from '../models/useBilling'
import BillingAccount from './BillingAccount'
import { checkoutOutcome, periodDateLabel, periodVerb } from '../utilities/subscription'
import { useOrgRole } from '../hooks/useOrgRole'
import { formatCents } from '../utilities/general'
import { describeBillingError } from '../utilities/billingError'
import {
  OVERAGE_LABEL,
  PRICING_MODEL,
  overageLine,
  planRows,
  unlimitedLine,
} from '../utilities/planLines'
import { byPlanOrder } from '../utilities/planOrder'
import CreditBlock from './CreditBlock'
import { useModalClose } from '../hooks/useModalClose'
import Modal from './Modal'
import { DOCS_BILLING_URL } from '../constants'

const CONTACT_URL = 'mailto:sales@nobgp.com?subject=noBGP%20Enterprise'

const capitalize = (s: string): string => s.replace(/^\w/, c => c.toUpperCase())

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  past_due: 'warning',
  unpaid: 'error',
  canceled: 'error',
  inactive: 'default',
}

type Props = { inline?: boolean }

export default function Billing({ inline }: Readonly<Props>) {
  const { open, handleClose } = useModalClose('/', inline)
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  const { can } = useOrgRole()
  const canManage = can('billing.manage') // Owner only — the router re-checks (ActionManageBilling)
  const {
    plan,
    plans,
    computeFromCentsPerHour,
    status,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    interval: servedInterval,
    card,
    invoices,
    hasMoreInvoices,
    summaryLoaded,
    summaryError,
    get,
    upgrade,
    openPortal,
    loadSummary,
    clearSummary,
  } = useBilling()
  // ⚠⚠ A HARD-CAPS PLAN GETS NO MONEY CONTROLS AT ALL — not disabled ones, and not an upsell in
  // their place (app#156). Benoit, 2026-08-27: "no spend cap on free, it does not make sense since
  // there is no overage or credit or auto-refill etc…". A hard-caps plan bills NO overage, so a cap
  // bounds nothing: a Free owner who saved $50 had reasonably concluded they could spend $50, when
  // what actually happens at the allowance is that their transfers STOP (nobgp-router#966). That is
  // a wrong promise about the customer's money, made through a CONTROL rather than through copy,
  // which is why the fix was to take the control away rather than reword it.
  //
  // ⚠ THE CAP IS NOW EDITED INSIDE `CreditBlock`, beside the trigger and the amount it bounds
  // (app#190) — it is a ceiling on AUTOMATIC refills (nobgp-router#1195), so it belongs with the
  // rule that spends it and not in a section of its own. This flag is all that is left here.
  //
  // ⚠ Derived with `isFreePlan`, the predicate the banner, Usage and the SSO upsell already share,
  // so a renamed or newly-added hard-capped plan reads the same here as everywhere else (app#127).
  // No second way of asking "is this Free".
  //
  // ⚠ A NULL PLAN IS THE THIRD STATE AND THIS SPELLS IT (app#141): `isFreePlan(null)` is false, so
  // an unknown plan KEEPS the controls. That is deliberate. Hiding them from an org that can
  // actually bill overage takes away its only ceiling on a bill; showing them to an unknown plan is
  // at worst the cosmetic defect above. The two errors do not cost the same, so the unknown case
  // sits with the cheaper one.
  const freePlan = isFreePlan(plan)
  // 'renews' or 'ends' — the one line a cancelled subscription used to get wrong.
  //
  // ⚠⚠ GATED ON THE SUMMARY HAVING LANDED, and that is the app#216 rule applied to the field that
  // decides between the two verbs. `cancelAtPeriodEnd` is written ONLY by `loadSummary`, which is
  // Owner-only, so for an admin or a member it keeps its initial `false` — and this line then told
  // the admin of an organization that had already cancelled that the plan RENEWS. That is
  // router#1133 again, on the reader the fix never covered. Until the read answers there is no
  // verb, for anybody.
  const verb = summaryLoaded ? periodVerb(status, cancelAtPeriodEnd, currentPeriodEnd, plan ? plan.hardCaps : null) : null
  // ⚠ The absolute date first, in UTC, with the zone named, and the relative form after it
  // (app#208). '' when the stamp is missing or unparseable, and then the verb is dropped too:
  // "renews" with no date beside it is a promise with no term.
  const periodDate = periodDateLabel(currentPeriodEnd)

  // The checkout choice — what the toggle holds for an org that can still use it. See `interval`.
  const [intervalChoice, setIntervalChoice] = useState<'monthly' | 'annual'>('monthly')
  const [busy, setBusy] = useState<string>() // identifier of the in-flight action
  const [error, setError] = useState<string>()
  const [searchParams, setSearchParams] = useSearchParams()
  const checkoutReturn = searchParams.get('status') // 'success' | 'cancel' from the Checkout redirect

  useEffect(() => {
    void get()
  }, [activeOrgId])

  // ⚠⚠ CLEARED ON EVERY ORG SWITCH, BEFORE ANYTHING IS FETCHED. The billing store is app-wide and
  // `get()` does not touch these fields, so without this the card and invoices of the org the
  // customer just left stay on the screen — and for an org they are NOT the Owner of, the read
  // below never runs, so the previous org's money is what they keep reading. The field list is
  // the store's (`clearSummary`), because `Usage.tsx` clears the same thing (app#268).
  useEffect(() => {
    clearSummary()
  }, [activeOrgId])

  // ⚠ Owner-only on the router, so a member must not call it: the read would be refused and the
  // error would be the only thing that changed on their screen.
  useEffect(() => {
    if (canManage) void loadSummary()
  }, [activeOrgId, canManage])

  // An org with a live subscription must change plans via the portal — create_checkout refuses a
  // second subscription (the router returns failed_precondition). Checkout is only the first upgrade.
  const hasSubscription = status === 'active' || status === 'past_due'
  // ⚠⚠ THE TOGGLE'S VALUE IS THE SUBSCRIPTION'S INTERVAL, NOT THE LITERAL IT WAS SEEDED WITH
  // (app#264). The group is disabled for a subscribed org — correctly, see the caption — and it
  // was disabled AT `'monthly'`, so an annual subscriber read "Monthly" selected and every card
  // priced `/ mo`, their own plan's `$20.00 / mo · Current plan` among them, when they pay $200
  // a year. The served interval (router#1304) decides for a subscribed org; the checkout choice
  // stays the value for an org that can still choose. ⚠ A null interval — an older router, a
  // reader who cannot load the summary — keeps the choice, which is exactly today's behaviour.
  const interval: 'monthly' | 'annual' =
    hasSubscription && servedInterval ? (servedInterval === 'year' ? 'annual' : 'monthly') : intervalChoice
  // ⚠ DISMISSING CLEARS THE PARAMETER, not just the alert. The state lives in the URL, so hiding
  // it in component state would bring it back on the next reload — and a stale "payment received"
  // is a worse thing to resurrect than most.
  const clearCheckoutReturn = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    setSearchParams(next, { replace: true })
  }

  // ⚠⚠ THE RETURN BELONGS TO ONE ORGANIZATION, AND THE PARAMETER DOES NOT KNOW THAT. `setActiveOrg`
  // re-scopes the stores and does NOT navigate, so `?status=success` survives an org switch while
  // the page stays put — and the `status` `checkoutOutcome` reads is then the NEW org's. Without
  // this, a customer who pays for A and switches to B reads "Payment received — this organization
  // is on the Pro plan" about an organization nobody just paid for. The `cancelled` alert carries
  // the same wrong claim one step quieter.
  //
  // ⚠ THE FIRST NON-NULL ORG WINS, NOT THE FIRST RENDER. A Checkout return is a full page load and
  // `activeOrgId` is null until `useOrganizations.get()` lands, so latching on mount would latch
  // null and suppress the alert the return exists to show.
  //
  // ⚠ STATE AND NOT A REF, so the divergence is visible to the render that follows the switch. A
  // ref would clear the parameter one effect too late and show the wrong sentence for a frame,
  // which on this alert is the whole defect in miniature.
  const [returnOrg, setReturnOrg] = useState<string | null>(null)
  useEffect(() => {
    if (!activeOrgId) return
    if (!returnOrg) {
      setReturnOrg(activeOrgId)
      return
    }
    // The return is no longer about the organization on screen — drop it from the URL as well, so
    // a reload does not resurrect it.
    if (returnOrg !== activeOrgId && checkoutReturn) clearCheckoutReturn()
  }, [activeOrgId, returnOrg, checkoutReturn])

  // ⚠⚠ THREE STATES, NOT ONE (app#247). `finalizing` covered only the wait between Stripe
  // returning and the webhook landing; once it landed nothing took its place, so the whole
  // acknowledgement that money had moved was that the chip now reads PRO. `status=cancel` was read
  // and never used.
  //
  // ⚠ SUPPRESSED ONCE THE RETURN IS NOT ABOUT THE ORGANIZATION ON SCREEN — see `returnOrg` above.
  const outcome = returnOrg && returnOrg !== activeOrgId ? null : checkoutOutcome(checkoutReturn, status)

  // ⚠⚠ THE THROWN TEXT IS NEVER RENDERED (app#200). `e.message` here is a GO error — the credit
  // path put a `sql: Scan error … converting driver.Value type string` in a red alert on prod,
  // naming an internal SQL function, an org id and a Go type, and answering none of the three
  // questions a failed money action raises. `describeBillingError` maps the router's cause to a
  // sentence with a remedy; the original goes to the console, which is the only place it belongs.
  //
  // ⚠ Every action on THIS panel is `manage`: the plan picker opens Stripe Checkout and the rest
  // opens the portal or pages invoices, so no press here charges a card and none of them may
  // raise the question. `CreditBlock`'s Buy is the one that does.
  const run = async (id: string, fn: () => Promise<void>) => {
    setError(undefined)
    setBusy(id)
    try {
      await fn()
    } catch (e) {
      console.error('billing action failed', id, e)
      setError(describeBillingError(e, 'manage'))
    } finally {
      setBusy(undefined)
    }
  }

  // ⚠⚠ THE PICKER SHOWS WHAT IS STILL SOLD. `plans.offered` (nobgp-router migration 0106)
  // soft-retires a plan: the row stays so `organizations.plan_id` and historical invoices keep
  // resolving, and this is the surface that is supposed to hide it. Without the filter the
  // retired `business` card renders with a live Upgrade button, and the router's `create_checkout`
  // — which asks the catalog rather than a hardcoded pair since the same migration — answers
  // `unknown plan "business" — this deployment sells pro`. A dead end the user had to click.
  //
  // ⚠ `!== false`, not truthy: before 0106 applies the column does not exist, so the key is
  // absent and `undefined` must read as offered. The org's CURRENT plan is never filtered out —
  // an org sitting on a retired plan still has to see which one it is on.
  //
  // The order is `byPlanOrder`: cheapest first, quote-only Enterprise last. A plain price sort put
  // Enterprise SECOND — it carries no list price, so its zero sorted beside Free's.
  const orderedPlans = [...plans]
    .filter(p => p.offered !== false || p.id === plan?.id)
    .sort(byPlanOrder)

  return (
    <Modal open={open} inline={inline} maxWidth="md" onClose={inline ? undefined : handleClose}>
      <DialogContent>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
            mb: 2
          }}>
          {/* ⚠⚠ THE PANEL'S ONE TOP-LEVEL HEADING (app#212). MUI's `variant` sets the type scale
              AND the tag, so every title on this panel came out an `h6` and the credit balance an
              `h4` — a document outline where the balance sat two levels above the panel holding
              it. `component` is the half that was missing: the size is a design choice, the level
              is a structural fact. `h2` is what MUI's own `DialogTitle` uses, and every section
              title below it is an `h3`. */}
          <Typography variant="subtitle1" component="h2" color="secondary">
            Billing
          </Typography>
          {plan && <Chip size="small" label={capitalize(plan.name)} />}
          <Chip size="small" color={STATUS_COLOR[status] ?? 'default'} variant="outlined" label={status.replace('_', ' ')} />
        </Stack>

        {!canManage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the organization Owner can change the plan or payment method. You can view the current plan and usage.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(undefined)}>
            {error}
          </Alert>
        )}
        {outcome === 'finalizing' && (
          <Alert severity="info" icon={<CircularProgress size="1rem" />} sx={{ mb: 2 }}>
            Finalizing your subscription… this updates automatically once payment is confirmed.
          </Alert>
        )}
        {/* ⚠⚠ IT NAMES THE PLAN AND NOT AN AMOUNT (app#247). The plan is safe: `status === 'active'`
            is the webhook's own signal, so the row already carries the plan that was bought. The
            AMOUNT is not — on this return the new invoice may not have arrived, and quoting
            `invoices[0]` would name a PREVIOUS charge as the one just made. A wrong number about
            the customer's money, on the alert that exists to reassure them about it, is worse than
            no number. It points at where the receipt will be instead. */}
        {outcome === 'subscribed' && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={clearCheckoutReturn}>
            Payment received — this organization is on {plan ? `the ${capitalize(plan.name)} plan` : 'its new plan'}. The
            receipt appears under Invoices below, and in the billing portal.
          </Alert>
        )}
        {/* ⚠ The abandoned path said nothing at all, so a customer who backed out had no word on
            whether a card had been charged. Stripe charges nothing until Subscribe is pressed. */}
        {outcome === 'cancelled' && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={clearCheckoutReturn}>
            Checkout was not completed, so nothing changed and no payment was taken.
          </Alert>
        )}

        {/* Current plan summary */}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 1
          }}>
          <Typography variant="body2" sx={{
            color: 'text.secondary'
          }}>
            {plan ? `${capitalize(plan.name)} plan` : 'No plan'}
            {/* ⚠⚠ THE PAIR, NEVER THE STATUS ALONE — see utilities/subscription.ts. */}
            {verb && periodDate && ` · ${verb} ${periodDate}`}
          </Typography>
          {hasSubscription && (
            <Button size="small" disabled={!canManage || !!busy} onClick={() => run('portal', openPortal)}>
              {busy === 'portal' ? <CircularProgress size="1rem" /> : 'Manage billing'}
            </Button>
          )}
        </Stack>
        {status === 'past_due' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Payment failed — update your card in the billing portal to avoid losing your subscription. Your nodes stay online.
          </Alert>
        )}

        {/* ⚠⚠ THE MODEL, ONCE, FOR ALL FOUR COLUMNS (app#219) — see `PRICING_MODEL`. It sits above
            the picker rather than on the cards so that each card only fills in its numbers, and
            above the interval toggle because it describes the pricing and not the term.

            ⚠ The link is the only documentation link on this panel (app#211, the Billing half),
            and it is what lets the cards carry facts instead of explanations. Hygiene matches the
            invoice link in `BillingAccount.tsx`: `target="_blank"` with `rel="noreferrer"`.

            ⚠ The URL lives in `constants.ts` because the usage panel links the same page (app#211,
            the Usage half), and the path is the half that is easy to get wrong. */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {PRICING_MODEL}{' '}
          <Link href={DOCS_BILLING_URL} target="_blank" rel="noreferrer" variant="body2">
            How billing works
          </Link>
        </Typography>

        {/* Interval toggle.

            ⚠⚠ A DISABLED CONTROL WITH NO EXPLANATION IS A DEAD END, AND THIS ONE HIDES A REAL
            PATH (Benoit, 2026-09-03: "how do I adjust my plan from monthly to annual if I want to,
            without changing plan"). The group is disabled for a subscribed org because
            `create_checkout` refuses a second checkout — the interval of a LIVE subscription is
            changed in Stripe's portal, which is what "Adjust plan" on every card opens. Nothing
            said so, so the one customer who wants the annual price is the one who reads a control
            that ignores them.

            ⚠ MEASURED IN STRIPE TEST MODE, 2026-09-03, on a real Pro annual subscription: the
            portal's update flow offers a Monthly/Yearly toggle on the same product, and switching
            takes effect at the next monthly boundary with the unused annual amount returned as an
            account balance credit. So this caption points at something that works — do not soften
            it to "contact us".

            ⚠ It appears only while the group is DISABLED. An org that can still use the toggle has
            no dead end to explain, and a sentence about the portal would be noise on the one
            screen where the toggle is the answer.

            ⚠⚠ AND IT BRANCHES ON `canManage`, BECAUSE "use Adjust plan" IS AN INSTRUCTION AND AN
            INSTRUCTION MUST BE ONE THE READER CAN FOLLOW. `billing.manage` is Owner-only, so every
            Adjust plan button on this panel is `disabled: !canManage`. An unbranched sentence
            therefore tells an Admin or a Member to use a control that is dead for all of them.

            ⚠ It is NOT the current plan's card they are sent to, and the tempting version of this
            note says it is. `action` returns `{ label: 'Current plan', disabled: true }` FIRST, so
            that card never says "Adjust plan" and is disabled for the Owner too — its own label is
            the explanation. The sentence points at the OTHER cards, which do carry the "Only the
            org Owner can manage billing" tooltip, because `canManage || current` is false there
            when `!canManage`.

            ⚠ So the dead end is softer than "no explanation anywhere" — it is an instruction whose
            refusal is discoverable only by hovering a different card. Saying it in the sentence
            costs nothing and does not depend on a hover, which a touch reader does not have.

            ⚠ THE ROW WRAPS BELOW `sm`. The panel is a dialog and at phone width the paper is about
            310 px, where "Monthly" plus "Annual · 2 months free" already takes most of it; a
            sentence beside them in an unwrapping row gets squeezed to a column of single words.
            The `flex-end` for the no-caption case is preserved, so an org that can still use the
            toggle sees exactly what it saw before. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            justifyContent: hasSubscription ? 'space-between' : 'flex-end',
            alignItems: { xs: hasSubscription ? 'flex-start' : 'flex-end', sm: 'center' },
            gap: 1,
            mb: 1
          }}>
          {hasSubscription && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {canManage
                ? 'Monthly or annual is chosen when you subscribe. To change it now, use Adjust plan.'
                : 'Monthly or annual is chosen when you subscribe. Only the org Owner can change it.'}
            </Typography>
          )}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={interval}
            onChange={(_, v) => v && setIntervalChoice(v)}
            disabled={hasSubscription}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annual">Annual · 2 months free</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Plan picker.

            ⚠⚠ FOUR ACROSS ONLY AT `lg`, AND THAT IS A MEASUREMENT. The panel is a `maxWidth="md"`
            dialog, so its paper is `min(900, 100vw - 64)`: at the `md` breakpoint itself the four
            columns are 169 px wide — 135 px of content — and "1,000 small-hours / mo" wants 137.
            Business alone wrapped, which pushed ITS rows down and broke the row-for-row comparison
            the cards exist for (app#219). Above `lg` the paper is pinned at 900 and a column is
            197 px, where every value fits with room to spare. Between the two the cards pair up
            two-across at ~377 px rather than shrink past their own content.

            ⚠ A GRID AND NOT A `Stack`, because a Stack cannot wrap into rows. Grid items stretch
            to the tallest card in their row by default, which is what lets each card push its
            button to its own foot and still line the four buttons up. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 1,
          }}>
          {orderedPlans.map(p => (
            <PlanCard
              key={p.id}
              plan={p}
              computeFromCentsPerHour={computeFromCentsPerHour}
              interval={interval}
              current={plan?.id === p.id}
              hasSubscription={hasSubscription}
              canManage={canManage}
              busy={busy}
              onUpgrade={() => run(`upgrade-${p.name}`, () => upgrade(p.name, interval))}
              onPortal={() => run('portal', () => openPortal('subscription_update'))}
            />
          ))}
        </Box>

        {/* The credit balance, its breakdown, buying and auto-reload (app#146 item 2, app#170).
            ⚠ THE SAME COMPONENT THE USAGE PAGE RENDERS, deliberately: app#170 repeats it on both
            panels because "what happens when this bar fills" is asked at the bar, while "what have
            I agreed to spend" is asked here. One component means the two panels cannot come to
            describe one balance two ways. It renders nothing for the exempt tier or on a
            deployment that invoices in arrears — so it needs no gate of its own here.

            ⚠⚠ FREE IS READ-ONLY HERE, AND HIDDEN ON THE USAGE PAGE (app#265). The credit is money
            the customer paid for and it does not expire, and an organization that cancelled kept
            it — $3.50 measured on prod — with no screen left that showed it. `readOnly` renders
            the balance and the purchase list when there is either, and no control: Free is
            hard-capped and spends no credit, so Buy there sells something the organization cannot
            use until it subscribes again. */}
        <CreditBlock isFree={freePlan} readOnly={freePlan} />

        {/* The card, the invoices and cancellation (app#169).

            ⚠⚠ RENDERED FOR ANY ORGANIZATION THAT HAS A STRIPE CUSTOMER, not only one with a live
            subscription (app#265). A cancelled organization moves to Free and its card stays
            attached and its invoices stay issued — the summary read returns both — and hiding the
            section left the customer no way to see or remove the card, and no link to the
            receipts. The read itself answers the question: `GetSummary` returns an empty summary
            only for an org that never had a customer, so a card or an invoice IS the customer.
            `card` and `invoices` are written only by a landed read and cleared on every org
            switch above, so neither can be the previous org's.

            ⚠ An org that never subscribed still gets no section: it has no card, no invoice and
            nothing to cancel, and a section it can never fill is worse than no section. And a
            Free org with a customer gets no CANCELLATION — `hasSubscription` is what
            `BillingAccount` hides it on.

            ⚠ `summaryError` is in the gate too. Without it a Free org whose summary read FAILS
            sees nothing: no card, no invoices, no error and no portal link, because the failed
            read leaves `card` null and `invoices` empty. With it, `BillingAccount` renders its
            one-line error that names the portal. The trade-off: a Free org that never subscribed
            also sees that error caption during an outage. */}
        {(hasSubscription || (summaryLoaded && (summaryError || !!card || invoices.length > 0))) && (
          <Box sx={{ mt: 3 }}>
            <BillingAccount
              card={card}
              invoices={invoices}
              hasMoreInvoices={hasMoreInvoices}
              summaryError={summaryError}
              cancelAtPeriodEnd={cancelAtPeriodEnd}
              hasSubscription={hasSubscription}
              canManage={canManage}
              busy={busy}
              /* ⚠ The busy key names the BUTTON, and "Keep my plan" sends no flow — so keying on
                 `subscription_cancel` alone put the spinner on the Payment button while the
                 control the customer actually clicked sat unchanged. */
              onPortal={(flow?: PortalFlow) =>
                run(flow === 'payment_method_update' ? 'card' : 'cancel', () => openPortal(flow))
              }
              onLoadMore={() =>
                run('invoices', () => loadSummary(invoices[invoices.length - 1]?.id))
              }
            />
          </Box>
        )}
      </DialogContent>
    </Modal>
  )
}

function PlanCard({
  plan,
  computeFromCentsPerHour,
  interval,
  current,
  hasSubscription,
  canManage,
  busy,
  onUpgrade,
  onPortal,
}: Readonly<{
  plan: PlanRow
  computeFromCentsPerHour: number | null
  interval: 'monthly' | 'annual'
  current: boolean
  hasSubscription: boolean
  canManage: boolean
  busy?: string
  onUpgrade: () => void
  onPortal: () => void
}>) {
  const enterprise = !plan.metered
  const free = plan.hardCaps
  // ⚠⚠ THE ROWS ARE NOT THE SAME KIND OF THING, AND THIS CARD ONCE READ THEM AS IF THEY WERE
  // (app#136 item 1). Bandwidth and compute are billable ALLOWANCES; storage and devices are
  // LIMITS with no overage rate on any plan — migration 0108 dropped both rate columns.
  // `planLines.ts` carries the distinction in the row LABELS and the test states it as data.

  // Annual is DISPLAYED as 10× the monthly flat base — the "2 months free" the toggle advertises
  // (router migration 0031). The actual charge comes from the Stripe annual price object
  // (plans.stripe_price_base_annual), which create_checkout selects by `interval`; the plan row
  // carries no annual cents for the card to read, so this ×10 is a display mirror. If the annual
  // discount ever changes, update all three together: the Stripe price, the invariant, and this.
  // Free/Enterprise have no flat base price.
  const price = enterprise
    ? 'Custom'
    : free
      ? 'Free'
      : interval === 'annual'
        ? `${formatCents(plan.monthlyFeeCents * 10)} / yr`
        : `${formatCents(plan.monthlyFeeCents)} / mo`

  // ⚠ THE TYPE IS SPELLED OUT because the Enterprise branch carries an `href` and the others carry
  // an `onClick`. Inference would union the shapes and `action.href` would not compile.
  const action: { label: string; disabled: boolean; onClick?: () => void; href?: string } = (() => {
    if (current) return { label: 'Current plan', disabled: true }
    // ⚠⚠ GATED ON `canManage` LIKE EVERY OTHER PLAN CONTROL, AND THAT IS A DECISION RATHER THAN
    // BRANCH ORDER (app#213). This branch returned before `canManage` was read, so "Contact us"
    // stayed live for a member and an admin while every other button on the panel was dead and
    // the alert above them said only the Owner can change the plan. It is only a `mailto:`, so
    // either answer was defensible; the panel promises one rule, and starting a sales conversation
    // about a plan IS changing the plan. A member who wants to talk to sales reads the same
    // tooltip as everywhere else, so nothing is silent.
    //
    // ⚠⚠ AN ANCHOR, NOT `window.open` (app#213). `window.open(CONTACT_URL, '_blank', 'noreferrer')`
    // makes some browsers open a blank tab, hand the URL to the mail client, and leave the empty
    // tab behind. A control that leaves the app is a link: MUI switches `Button`'s root to `<a>`
    // when it is given an `href`, so the styling does not change.
    if (enterprise) return { label: 'Contact us', disabled: !canManage, href: CONTACT_URL }
    // A subscribed org changes plans (up, down, or to free) through the portal; create_checkout is
    // only for the first upgrade from a no-subscription org.
    // ⚠ A subscribed org changes plan through the portal's subscription_update flow — the button
    // says what it does and the customer lands on the plan chooser. `create_checkout` refuses a
    // second subscription, so this is still not a checkout.
    if (hasSubscription || free) return { label: 'Adjust plan', disabled: !canManage, onClick: onPortal }
    return { label: 'Upgrade', disabled: !canManage, onClick: onUpgrade }
  })()

  const disabled = action.disabled || !!busy

  const button = (
    <Button
      fullWidth
      variant={current ? 'outlined' : 'contained'}
      disabled={disabled}
      // ⚠ THE `href` IS DROPPED WHILE THE BUTTON IS DEAD. MUI puts `pointer-events: none` on a
      // disabled control, but an anchor that still carries an `href` can be reached another way.
      // With no `href` the root falls back to `<button disabled>`, which is what it should be.
      href={disabled ? undefined : action.href}
      onClick={action.onClick}
    >
      {busy === `upgrade-${plan.name}` || busy === 'portal' ? <CircularProgress size="1rem" /> : action.label}
    </Button>
  )

  return (
    <Box
      sx={({ palette, shape }) => ({
        p: 2,
        borderRadius: `${shape.borderRadius}px`,
        border: '1px solid',
        borderColor: current ? palette.primary.main : palette.divider,
        // ⚠ THE CARD IS A COLUMN SO THE BUTTON CAN BE PUSHED TO ITS FOOT. The grid above already
        // stretches the four cards to one height; what was ragged was the CONTENT inside them, so
        // the four CTAs sat at 266 / 283 / 266 / 216 px — 67 px apart on boxes of identical
        // height. `mt: 'auto'` below is what levels them, and it needs a flex column to act in.
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {/* ⚠⚠ `useFlexGap`, AND IT IS LOAD-BEARING RATHER THAN A STYLE CHOICE. A MUI Stack with a
          `spacing` prop emits `.MuiStack-root > :not(style):not(style) { margin: 0 }`, whose
          specificity outranks a child's own `sx` — so the `mt: 'auto'` that lifts the button to
          the foot of the card would be silently dead. `useFlexGap` spaces with `gap` and resets
          no margins. Same trap `CreditBlock`'s buttons sat in for months. */}
      <Stack spacing={1.5} useFlexGap sx={{ flex: 1 }}>
        {/* ⚠ THE PLAN NAME IS THE CARD'S HEADING, THE PRICE IS NOT (app#212). The price wants
            heading SIZE and is a value, so it keeps `h6`'s scale on a `p`. Both sit one level
            under the panel title. */}
        <Box>
          <Typography variant="subtitle2" component="h3">{capitalize(plan.name)}</Typography>
          <Typography variant="h6" component="p">{price}</Typography>
        </Box>
        {/* ⚠⚠ ONE SKELETON, IDENTICAL ON ALL FOUR CARDS (app#219). The rows are label/value and
            come from `planRows`, in the model's own order — the two METERS, then the one limit —
            so the four columns compare row for row and the reader can see that only three numbers
            differ. A grid rather than a `Stack` of sentences is what aligns the labels; `display:
            contents` lets one row contribute two cells, the same shape `CreditBlock` uses for its
            grant breakdown.

            ⚠⚠ LABEL AND VALUE FIT SIDE BY SIDE ONLY BECAUSE BOTH STRINGS WERE SHORTENED, and the
            two shortenings are not interchangeable. Measured at four cards across, where a card
            gives 150 px: the label column is set by its WIDEST member, `Bandwidth` at 56 px, and
            the 10 px gap leaves 84 px for the number — against a longest value of 67 px
            (`100 GB / mo`). The compute value (`$50 / mo`, app#331) has fewer characters. It did
            not fit before because `small-hours` made the longest value 122 px and `Storage limit`
            made the label column 67. Fixing only one of those still wraps.

            ⚠ THE 17 px OF SPARE IS THE WHOLE MARGIN — check both when you touch either string.
            Putting a unit back on the compute value, or `Storage limit` back on the label, can use
            it all.

            ⚠⚠ THE COMPUTE DETAIL LINE MUST NOT WRAP (app#331). It spans the whole card, 151 px of
            content at four across in the harness. "included, then from $0.02/h" (145 px) fit the
            harness and WRAPPED on the live preview at a 1524 px window, so the harness is not the
            whole margin: the real dialog is narrower. "then from $0.02/h" is 94 px, and the line
            is `nowrap` so a wrap cannot move the Storage row on one card only.

            ⚠⚠ EVERY PIXEL FIGURE IN THIS FILE CAME FROM THE HARNESS IN `harness/` — `npm run
            harness`, `?panel=billing` — and is re-derived there before changing a size here or a
            breakpoint above (`harness/vite.config.ts` says how it is built: the real stores written
            with `setState`, `src/supabaseClient` the one alias). `inline` keeps the panel in its
            real `maxWidth="md"` dialog — the paper width is what every number here depends on.
            Two different measurements, and the second is what sizes a column: read
            `offsetHeight / lineHeight` per Typography to find a WRAP, and for an INTRINSIC width
            clone a rendered Typography with `white-space: nowrap` and `width: auto`, swap its text
            and read `offsetWidth`. Measure at viewports 900, 1300 and 1400, in both colour schemes.
            ⚠ 900 is the case that catches you: it is the `md` breakpoint itself, where the paper is
            `100vw - 64` rather than the pinned 900.

            ⚠ The LABELS come from `planRows` and not from this JSX, and `PRICING_MODEL` now
            carries what the labels used to: "everything else is a plan limit, not a charge" is
            app#136's fact for all four cards. `planLines.test.ts` asserts it, and asserts that
            this panel renders it. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 1.25,
            rowGap: 0.4,
            alignItems: 'baseline',
          }}>
          {planRows(plan, computeFromCentsPerHour).map(row => (
            <Box key={row.label} sx={{ display: 'contents' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.label}
              </Typography>
              {/* ⚠ The value carries the weight — the reader is comparing numbers, and at caption
                  size colour alone did not separate them from their labels. Weight only: `body2`
                  is 79 px for `100 GB / mo` and would spend most of the 17 px of spare above. */}
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {row.value}
              </Typography>
              {/* ⚠ THE DETAIL SPANS BOTH COLUMNS (app#331). "then from $0.02/h" is too
                  wide for the value column at four cards across, and it must not widen the label
                  column. Every card renders the line (the exempt tier a non-breaking space), so
                  the rows under it stay level across the four cards. */}
              {row.detail && (
                <Typography
                  variant="caption"
                  sx={{ gridColumn: '1 / -1', color: 'text.secondary', lineHeight: 1.3, mt: -0.25, whiteSpace: 'nowrap' }}
                >
                  {row.detail}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        {/* ⚠⚠ THE HEADLINE, AND IT WAS A FOOTNOTE IN THE MIDDLE OF A LIST (app#219). "Unlimited
            devices, users, networks and services" is the strongest thing we sell, and it read with
            exactly the weight of the storage cap above it.

            ⚠ WEIGHT AND COLOUR ONLY — the `variant` stays `caption`. Heading hierarchy on this
            panel is app#212 and belongs to another branch; this is a line item promoted inside a
            card, not a heading. */}
        <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.4 }}>
          {unlimitedLine(plan)}
        </Typography>
        {/* ⚠⚠ ONE OVERAGE ROW, AND IT IS THE ROW THAT MUST STAY LEGIBLY DIFFERENT BETWEEN FREE
            AND PAID (app#147). Free STOPS and paid SLOWS; `overageLine` carries both words and
            `planLines.test.ts` pins each. Everything that used to surround them — two Free
            captions and a paid one, 90 lines of comment between them — is the documentation
            link above the picker now.

            ⚠⚠ ITS LABEL NAMES THE METER (app#160, app#163). Two of the rows above are
            ALLOWANCES, so this row sits under both, and its sentence is TRUE of bandwidth and
            FALSE of compute — past the compute allowance the machine stops and that node goes
            OFFLINE. `OVERAGE_LABEL` is what scopes it; do not shorten it back.

            ⚠ IT IS A CONSEQUENCE AND NOT A NUMBER, so it sits below a rule rather than in the
            comparison block. It is the only multi-line paragraph on the card and reading it as a
            fourth "row" is what made the block look ragged; the rule says where the facts end. */}
        <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.4 }}>
            {OVERAGE_LABEL}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
            {overageLine(plan)}
          </Typography>
        </Box>
        {/* ⚠ `mt: 'auto'` — see `useFlexGap` above, without which this line does nothing. */}
        <Box sx={{ mt: 'auto' }}>
          {canManage || current ? (
            button
          ) : (
            <Tooltip arrow title="Only the org Owner can manage billing">
              <span>{button}</span>
            </Tooltip>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

